import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { placeService } from '../../services/placeService';
import type { NaverPlaceFetchResponse, PlaceCreate, PlaceType } from '../../types';

const PLACE_TYPES: { value: PlaceType; label: string }[] = [
  { value: 'restaurant', label: '음식점' },
  { value: 'hospital', label: '병원' },
  { value: 'place', label: '일반' },
  { value: 'hairshop', label: '미용실' },
  { value: 'nailshop', label: '네일샵' },
  { value: 'accommodation', label: '숙박' },
];

export function PlaceCreatePage() {
  const navigate = useNavigate();

  // Naver URL input state
  const [naverUrl, setNaverUrl] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<PlaceCreate>>({
    place_type: 'place',
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Handle fetching Naver Place data
  const handleFetchNaverPlace = async () => {
    if (!naverUrl.trim()) {
      setFetchError('네이버 플레이스 URL을 입력해주세요.');
      return;
    }

    setFetchLoading(true);
    setFetchError(null);

    try {
      const data: NaverPlaceFetchResponse = await placeService.fetchNaverPlaceData(naverUrl.trim());

      setFormData({
        id: data.id,
        name: data.name,
        place_type: data.place_type,
        category: data.category || undefined,
        main_category: data.main_category || undefined,
        city: data.city || undefined,
        si: data.si || undefined,
        gu: data.gu || undefined,
        dong: data.dong || undefined,
        major_area: data.major_area || undefined,
        road_address: data.road_address || undefined,
        jibun_address: data.jibun_address || undefined,
        phone: data.phone || undefined,
        introduction: data.introduction || undefined,
        stations: data.stations?.map((s) => `${s.name} (${s.distance})`) || undefined,
        conveniences: data.conveniences || undefined,
        naver_url: data.naver_url,
        gdid_score1: data.gdid_score1 || undefined,
        gdid_score2: data.gdid_score2 || undefined,
        gdid_score3: data.gdid_score3 || undefined,
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setFetchError(
        error.response?.data?.detail || '네이버 플레이스 정보를 가져올 수 없습니다.'
      );
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle form field change
  const handleFieldChange = (field: keyof PlaceCreate, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.id || !formData.name) {
      setSubmitError('ID와 이름은 필수 항목입니다.');
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);

    try {
      const place = await placeService.createPlace(formData as PlaceCreate);
      navigate(`/places/${place.id}`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setSubmitError(
        error.response?.data?.detail || '플레이스 등록에 실패했습니다.'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/places')}
          sx={{ mb: 2 }}
        >
          목록으로 돌아가기
        </Button>
        <Typography variant="h4" fontWeight="bold">
          플레이스 등록
        </Typography>
      </Box>

      {/* Naver URL Input Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            네이버 플레이스 URL로 자동 입력
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            네이버 플레이스 URL을 입력하면 플레이스 정보가 자동으로 입력됩니다.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label="네이버 플레이스 URL"
              placeholder="https://map.naver.com/p/search/.../place/12345678"
              value={naverUrl}
              onChange={(e) => setNaverUrl(e.target.value)}
              disabled={fetchLoading}
              helperText="예: https://map.naver.com/p/search/강남맛집/place/12345678?..."
            />
            <Button
              variant="contained"
              startIcon={fetchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              onClick={handleFetchNaverPlace}
              disabled={fetchLoading || !naverUrl.trim()}
              sx={{ minWidth: 120, height: 56 }}
            >
              {fetchLoading ? '조회중...' : '정보 조회'}
            </Button>
          </Box>
          {fetchError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {fetchError}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Form Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            플레이스 정보
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="플레이스 ID"
                type="number"
                value={formData.id || ''}
                onChange={(e) => handleFieldChange('id', parseInt(e.target.value) || '')}
                helperText="네이버 플레이스 고유 ID"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="플레이스명"
                value={formData.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>업종</InputLabel>
                <Select
                  value={formData.place_type || 'place'}
                  label="업종"
                  onChange={(e) => handleFieldChange('place_type', e.target.value)}
                >
                  {PLACE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="카테고리"
                value={formData.category || ''}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                helperText="예: 음식점 > 한식 > 삼겹살"
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                주소 정보
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="시/도"
                value={formData.city || ''}
                onChange={(e) => handleFieldChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="구/군"
                value={formData.gu || ''}
                onChange={(e) => handleFieldChange('gu', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="동/읍/면"
                value={formData.dong || ''}
                onChange={(e) => handleFieldChange('dong', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="도로명 주소"
                value={formData.road_address || ''}
                onChange={(e) => handleFieldChange('road_address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="지번 주소"
                value={formData.jibun_address || ''}
                onChange={(e) => handleFieldChange('jibun_address', e.target.value)}
              />
            </Grid>

            {/* Contact & Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                연락처 및 상세 정보
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="전화번호"
                value={formData.phone || ''}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="네이버 URL"
                value={formData.naver_url || ''}
                onChange={(e) => handleFieldChange('naver_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="소개"
                value={formData.introduction || ''}
                onChange={(e) => handleFieldChange('introduction', e.target.value)}
              />
            </Grid>

            {/* GDID Scores */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                GDID 점수 (자동 조회, 수정 불가)
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Score 1 (관련도)"
                value={formData.gdid_score1 || ''}
                InputProps={{ readOnly: true }}
                inputProps={{ step: '0.000001' }}
                sx={{
                  backgroundColor: 'action.disabledBackground',
                  '& .MuiInputBase-input': { color: 'text.secondary' },
                }}
                helperText="네이버 API에서 자동 조회됩니다"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Score 2 (인기도)"
                value={formData.gdid_score2 || ''}
                InputProps={{ readOnly: true }}
                inputProps={{ step: '0.000001' }}
                sx={{
                  backgroundColor: 'action.disabledBackground',
                  '& .MuiInputBase-input': { color: 'text.secondary' },
                }}
                helperText="네이버 API에서 자동 조회됩니다"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Score 3 (품질)"
                value={formData.gdid_score3 || ''}
                InputProps={{ readOnly: true }}
                inputProps={{ step: '0.000001' }}
                sx={{
                  backgroundColor: 'action.disabledBackground',
                  '& .MuiInputBase-input': { color: 'text.secondary' },
                }}
                helperText="네이버 API에서 자동 조회됩니다"
              />
            </Grid>
          </Grid>

          {/* Error & Submit */}
          {submitError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate('/places')}>
              취소
            </Button>
            <Button
              variant="contained"
              startIcon={submitLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={submitLoading || !formData.id || !formData.name}
            >
              {submitLoading ? '등록중...' : '플레이스 등록'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
